import ExcelJS from "exceljs";

const HEADER_FILL = "FF82CAEC";
const MODULE_FILL = "FFC1E5F5";
const MIN_FILL = "FFD6ECF6";   // mid-pale blue (shade of HEADER_FILL)
const MAX_FILL = "FFEAF5FB";   // very pale blue
const BORDER_COLOR = "FF000000";
const TEXT_RED = "FFCC0000";
const FONT_NAME = "Arial";
const FONT_SIZE = 11;

const MARGIN_COL = 1;
const MODULE_COL = 2;
const KPI_COL = 3;
const UNIT_COL = 4;
const PERIOD_COL_START = 5;

export const buildTriggerWorkbook = async ({
  sortedModules,
  locations,
  periods,
  financialYearLabel,
}) => {
  const workbook = new ExcelJS.Workbook();

  const blackBorder = {
    top: { style: "thin", color: { argb: BORDER_COLOR } },
    bottom: { style: "thin", color: { argb: BORDER_COLOR } },
    left: { style: "thin", color: { argb: BORDER_COLOR } },
    right: { style: "thin", color: { argb: BORDER_COLOR } },
  };
  const baseFont = { name: FONT_NAME, size: FONT_SIZE, color: { argb: "FF000000" } };
  const boldFont = { ...baseFont, bold: true };

  const periodColCount = periods.length * 2;
  const lastDataCol = PERIOD_COL_START + periodColCount - 1;

  const PERIOD_HEADER_ROW = 4;
  const SUB_HEADER_ROW = 5;
  const DATA_START_ROW = 6;

  for (const location of locations) {
    const sheet = workbook.addWorksheet(location.unitCode);
    sheet.views = [{ showGridLines: false }];

    // Column widths — column 1 is an empty margin (Quantative-style)
    const KPI_WIDTH = 50;
    const MODULE_WIDTH = Math.round((KPI_WIDTH * 2) / 3);
    sheet.getColumn(MARGIN_COL).width = 4;
    sheet.getColumn(MODULE_COL).width = MODULE_WIDTH;
    sheet.getColumn(KPI_COL).width = KPI_WIDTH;
    sheet.getColumn(UNIT_COL).width = 13;
    for (let i = 0; i < periodColCount; i++) {
      const periodIdx = Math.floor(i / 2);
      const label = periods[periodIdx]?.label ?? "";
      // Each Min/Max column holds ~half the period label, with padding
      sheet.getColumn(PERIOD_COL_START + i).width = Math.max(Math.ceil(label.length / 2) + 6, 12);
    }

    // Rows 1–2: visual top margin
    sheet.getRow(1).height = 8;
    sheet.getRow(2).height = 8;

    // Row 3: financial year
    const fyRow = sheet.getRow(3);
    fyRow.height = 20;
    const fyCell = fyRow.getCell(MODULE_COL);
    fyCell.value = `Financial Year: ${financialYearLabel}`;
    fyCell.font = { name: FONT_NAME, size: FONT_SIZE, bold: true, color: { argb: TEXT_RED } };
    fyCell.alignment = { vertical: "middle", horizontal: "left" };

    // Row 4 + 5: two-row header
    //   Row 4 — Module / KPI / Unit (merged into row 5), then one merged cell per period
    //   Row 5 — empty under Module/KPI/Unit (merged), then "Min" / "Max" sub-headers
    const periodHeaderRow = sheet.getRow(PERIOD_HEADER_ROW);
    const subHeaderRow = sheet.getRow(SUB_HEADER_ROW);
    periodHeaderRow.height = 24;
    subHeaderRow.height = 22;

    // Module / KPI / Unit cells: merged vertically across both header rows
    const fixedHeaders = [
      { col: MODULE_COL, label: "Module" },
      { col: KPI_COL, label: "KPI" },
      { col: UNIT_COL, label: "Unit" },
    ];
    for (const { col, label } of fixedHeaders) {
      sheet.mergeCells(PERIOD_HEADER_ROW, col, SUB_HEADER_ROW, col);
      const cell = sheet.getCell(PERIOD_HEADER_ROW, col);
      cell.value = label;
      cell.font = boldFont;
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
      cell.border = blackBorder;
    }

    // Per-period: merged period label on row 4, then Min/Max on row 5
    for (let p = 0; p < periods.length; p++) {
      const minCol = PERIOD_COL_START + p * 2;
      const maxCol = minCol + 1;

      sheet.mergeCells(PERIOD_HEADER_ROW, minCol, PERIOD_HEADER_ROW, maxCol);
      const periodCell = sheet.getCell(PERIOD_HEADER_ROW, minCol);
      periodCell.value = periods[p].label;
      periodCell.font = boldFont;
      periodCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      periodCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
      periodCell.border = blackBorder;

      const minCell = sheet.getCell(SUB_HEADER_ROW, minCol);
      minCell.value = "Min";
      minCell.font = boldFont;
      minCell.alignment = { horizontal: "center", vertical: "middle" };
      minCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: MIN_FILL } };
      minCell.border = blackBorder;

      const maxCell = sheet.getCell(SUB_HEADER_ROW, maxCol);
      maxCell.value = "Max";
      maxCell.font = boldFont;
      maxCell.alignment = { horizontal: "center", vertical: "middle" };
      maxCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: MAX_FILL } };
      maxCell.border = blackBorder;
    }

    // Data rows with a spacer row between modules
    let rowIdx = DATA_START_ROW;
    let firstModule = true;

    for (const [moduleName, items] of sortedModules) {
      if (!firstModule) {
        sheet.getRow(rowIdx).height = 11;
        rowIdx++;
      }
      firstModule = false;

      const moduleStartRow = rowIdx;

      for (const item of items) {
        const row = sheet.getRow(rowIdx);
        row.height = Math.max(22, Math.min(60, 16 + Math.floor(item.title.length / 50) * 14));

        row.getCell(KPI_COL).value = item.title;
        row.getCell(UNIT_COL).value = item.defaultUnit;

        const unitOptions = item.supportedUnits.map((u) => u.unit).filter(Boolean);
        if (unitOptions.length > 0) {
          row.getCell(UNIT_COL).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${unitOptions.join(",")}"`],
            showErrorMessage: true,
            errorTitle: "Invalid unit",
            error: `Allowed: ${unitOptions.join(", ")}`,
          };
        }

        for (let c = MODULE_COL; c <= lastDataCol; c++) {
          const cell = row.getCell(c);
          cell.font = baseFont;
          cell.border = blackBorder;
        }
        row.getCell(KPI_COL).alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
        row.getCell(UNIT_COL).alignment = { wrapText: true, vertical: "middle", horizontal: "center" };

        for (let i = 0; i < periodColCount; i++) {
          const cell = row.getCell(PERIOD_COL_START + i);
          cell.numFmt = "#,##0.##########";
          cell.alignment = { horizontal: "right", vertical: "middle" };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: i % 2 === 0 ? MIN_FILL : MAX_FILL },
          };
        }

        rowIdx++;
      }

      const moduleEndRow = rowIdx - 1;
      if (moduleEndRow >= moduleStartRow) {
        if (moduleEndRow > moduleStartRow) {
          sheet.mergeCells(moduleStartRow, MODULE_COL, moduleEndRow, MODULE_COL);
        }
        const moduleCell = sheet.getCell(moduleStartRow, MODULE_COL);
        moduleCell.value = moduleName;
        moduleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: MODULE_FILL } };
        moduleCell.font = boldFont;
        moduleCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true, indent: 1 };
        moduleCell.border = blackBorder;
      }
    }
  }

  const buf = await workbook.xlsx.writeBuffer();
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
